import { Routes } from 'constants/pages';
import _ from 'lodash';
import React from 'react';
import { WatchListFilters } from 'services/clients/watchList';
import { CurrentContainerStatus } from 'types/Watchlist';

export enum PresetView {
  Active = 'active',
  Archive = 'archive',
  Availability = 'availability',
  EmptyReturns = 'emptyReturns',
  DemurrageDue = 'demurrageDue',
  Terminated = 'terminated',
  WithHolds = 'withHolds',
  AvailableForPickup = 'availableForPickup',
  OnVessel = 'onVessel',
  OutstandingPerDiem = 'outstandingPerDiem',
  LessThanTwoFreeDays = 'lessThanTwoFreeDays',
  MoreThanTwoFreeDays = 'moreThanTwoFreeDays',
}

interface PresetDefinition {
  access: ('route' | 'dropdown')[];
  label: string;
  filters: WatchListFilters;
  presetView: PresetView;
  tooltip: string;
}

export const PRESET_DEFINITIONS: Record<PresetView, PresetDefinition> = {
  [PresetView.Active]: {
    access: ['dropdown', 'route'],
    label: 'All active shipments',
    filters: {},
    presetView: PresetView.Active,
    tooltip: 'All containers currently tracked.',
  },
  [PresetView.Archive]: {
    access: ['route'],
    label: 'Archive',
    filters: { unwatched: true },
    presetView: PresetView.Archive,
    tooltip: 'All containers you are no longer tracking.',
  },
  [PresetView.Availability]: {
    access: ['dropdown'],
    label: 'At terminal',
    filters: {
      containerStatus: [CurrentContainerStatus.AtTerminal],
    },
    presetView: PresetView.Availability,
    tooltip: 'All containers at the terminal.',
  },
  [PresetView.AvailableForPickup]: {
    access: ['dropdown'],
    label: 'Available for pickup',
    filters: {
      availableForPickup: true,
    },
    presetView: PresetView.AvailableForPickup,
    tooltip: 'All containers at the terminal and ready to be picked up.',
  },
  [PresetView.OutstandingPerDiem]: {
    access: ['dropdown'],
    label: 'Outstanding per diem',
    filters: {
      hasPerDiemFees: true,
      containerStatus: [CurrentContainerStatus.GatedOut],
    },
    presetView: PresetView.OutstandingPerDiem,
    tooltip: 'Your gated out containers that are past LFD and incurring per diem charges.',
  },
  [PresetView.EmptyReturns]: {
    access: ['dropdown'],
    label: 'Gated out',
    filters: {
      containerStatus: [CurrentContainerStatus.GatedOut],
    },
    presetView: PresetView.EmptyReturns,
    tooltip: 'All containers out gated.',
  },
  [PresetView.OnVessel]: {
    access: ['dropdown'],
    label: 'On vessel',
    filters: {
      containerStatus: [CurrentContainerStatus.OnVessel],
    },
    presetView: PresetView.OnVessel,
    tooltip: 'All containers currently on a steamship line vessel.',
  },
  [PresetView.DemurrageDue]: {
    access: ['dropdown'],
    label: 'Outstanding demurrage',
    filters: {
      demurrageDue: true,
    },
    presetView: PresetView.DemurrageDue,
    tooltip: 'The containers that are currently at the terminal and have unpaid demurrage fees.',
  },
  [PresetView.Terminated]: {
    access: ['dropdown'],
    label: 'Terminated',
    filters: {
      containerStatus: [CurrentContainerStatus.Terminated],
    },
    presetView: PresetView.Terminated,
    tooltip: 'All containers that have been returned empty.',
  },
  [PresetView.WithHolds]: {
    access: ['dropdown'],
    label: 'With holds',
    filters: {
      holdTypes: ['all'],
    },
    presetView: PresetView.WithHolds,
    tooltip:
      'All containers that currently have holds and need to be cleared before they can become available for pickup.',
  },
  [PresetView.LessThanTwoFreeDays]: {
    access: ['dropdown'],
    label: '≤ 2 free days remaining',
    filters: {
      estPerDiemLfd: {
        startDate: null,
        endDate: '+2:days',
        exactDate: null,
        includeEndDate: true,
        includeStartDate: null,
      },
    },
    presetView: PresetView.LessThanTwoFreeDays,
    tooltip: 'All containers that have less than 2 free days remaining.',
  },
  [PresetView.MoreThanTwoFreeDays]: {
    access: ['dropdown'],
    label: '> 2 free days remaining',
    filters: {
      estPerDiemLfd: {
        startDate: '+2:days',
        endDate: null,
        exactDate: null,
        includeEndDate: null,
        includeStartDate: null,
      },
    },
    presetView: PresetView.MoreThanTwoFreeDays,
    tooltip: 'All containers that have more than 2 free days remaining.',
  },
};

export enum UnwatchedState {
  OnlyUnwatched = 'onlyUnwatched',
  OnlyWatched = 'onlyWatched',
  WatchedAndUnwatched = 'watchedAndUnwatched',
}

export const UnwatchedStateMapping: { [key in UnwatchedState]: boolean | null } & {
  [key: string]: boolean | null;
} = {
  [UnwatchedState.OnlyUnwatched]: true,
  [UnwatchedState.OnlyWatched]: false,
  [UnwatchedState.WatchedAndUnwatched]: null,
};

export const encodePresetFilters = (presetDefinition: PresetDefinition) => {
  // Stringifies filters that are objects, but not arrays
  const stringified = Object.entries(presetDefinition.filters).reduce((acc, [key, value]) => {
    if (_.isObject(value) && !_.isArray(value)) return { ...acc, [key]: JSON.stringify(value) };
    return { ...acc, [key]: value };
  }, {});
  const searchParams = new URLSearchParams(stringified as Record<string, string>);
  const to = `${Routes.ShipmentsActive}?${searchParams.toString()}`;
  return { ...presetDefinition, to };
};

export const usePreset = (presetView: PresetView) => {
  const definition = PRESET_DEFINITIONS[presetView];

  return React.useMemo(() => {
    return encodePresetFilters(definition);
  }, [definition]);
};
